import { InteractionRequiredAuthError, PublicClientApplication, } from '@azure/msal-browser';
export class AuthRequiredError extends Error {
    constructor(reason = 'unauthenticated') {
        super('Not authenticated');
        this.name = 'AuthRequiredError';
        this.reason = reason;
    }
}
export function dispatchAuthRequired(reason) {
    window.dispatchEvent(new CustomEvent('auth:required', { detail: { reason } }));
}
export class ApiService {
    constructor(apiBaseUrl, authConfig) {
        this.initialized = false;
        this.apiBaseUrl = apiBaseUrl;
        if (!apiBaseUrl) {
            this.apiScope = null;
            this.msalInstance = null;
            return;
        }
        if (!authConfig?.clientId || !authConfig.authority || !authConfig.apiScope) {
            throw new Error('Missing required Microsoft authentication configuration');
        }
        let authority;
        try {
            authority = new URL(authConfig.authority);
        }
        catch {
            throw new Error('Microsoft authentication authority must be a valid URL');
        }
        if (authority.protocol !== 'https:') {
            throw new Error('Microsoft authentication authority must use HTTPS');
        }
        this.apiScope = authConfig.apiScope;
        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: authConfig.clientId,
                authority: authority.toString(),
                knownAuthorities: [authority.hostname],
                redirectUri: window.location.origin,
                postLogoutRedirectUri: window.location.origin,
            },
            cache: {
                cacheLocation: 'sessionStorage',
            },
        });
    }
    async initialize() {
        if (!this.msalInstance) {
            this.initialized = true;
            return;
        }
        if (this.initialized)
            return;
        await this.msalInstance.initialize();
        const redirectResult = await this.msalInstance.handleRedirectPromise();
        const account = redirectResult?.account ?? this.msalInstance.getActiveAccount() ?? this.getFirstAccount();
        if (account) {
            this.msalInstance.setActiveAccount(account);
        }
        this.initialized = true;
    }
    async isAuthenticated() {
        if (!this.msalInstance)
            return true;
        this.ensureInitialized();
        return this.getAccount() !== null;
    }
    async login() {
        if (!this.msalInstance || !this.apiScope)
            return;
        this.ensureInitialized();
        await this.msalInstance.loginRedirect({ scopes: [this.apiScope] });
    }
    async logout(postLogoutRedirectUri) {
        if (!this.msalInstance)
            return;
        this.ensureInitialized();
        await this.msalInstance.logoutRedirect({
            account: this.getAccount() ?? undefined,
            postLogoutRedirectUri: postLogoutRedirectUri ?? window.location.origin,
        });
    }
    async getAuthHeaders() {
        const accessToken = await this.getAccessToken();
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    }
    async fetch(endpoint, options = {}) {
        try {
            const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
            const headers = new Headers(options.headers);
            const accessToken = await this.getAccessToken();
            if (accessToken) {
                headers.set('Authorization', `Bearer ${accessToken}`);
            }
            const response = await fetch(`${this.apiBaseUrl}${normalizedEndpoint}`, {
                ...options,
                headers,
            });
            if (!response.ok) {
                if (response.status === 401) {
                    dispatchAuthRequired('expired');
                }
                const errorText = await response.text().catch(() => 'Unknown error');
                throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return response.headers.get('content-type')?.includes('application/json')
                ? (await response.json())
                : response;
        }
        catch (error) {
            if (error instanceof AuthRequiredError) {
                dispatchAuthRequired(error.reason);
            }
            throw error;
        }
    }
    ensureInitialized() {
        if (!this.initialized) {
            throw new Error('ApiService must be initialized before use');
        }
    }
    getFirstAccount() {
        return this.msalInstance?.getAllAccounts()[0] ?? null;
    }
    getAccount() {
        return this.msalInstance?.getActiveAccount() ?? this.getFirstAccount();
    }
    async getAccessToken() {
        if (!this.msalInstance || !this.apiScope)
            return null;
        this.ensureInitialized();
        const account = this.getAccount();
        if (!account)
            throw new AuthRequiredError('unauthenticated');
        try {
            const result = await this.msalInstance.acquireTokenSilent({
                account,
                scopes: [this.apiScope],
            });
            return result.accessToken;
        }
        catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // We had an account, so the user was signed in at some point;
                // silent renewal failing is an expiry, not a first visit.
                await this.msalInstance.acquireTokenRedirect({
                    account,
                    scopes: [this.apiScope],
                });
                throw new AuthRequiredError('expired');
            }
            throw error;
        }
    }
}
